import { drawAvatar, type Look } from './avatar';

// ─────────────────────────────────────────────────────────────────────────────
// The race cars (components/RacePlayer.tsx), Hill Climb Racing style: each one
// handles differently. Everyone has the Racer; the others are won in the
// Sunken Fortress (one new car with each week's first clear, 20261041).
// Units are tiles and seconds; engine, brake and reverse are per wheel.
// ─────────────────────────────────────────────────────────────────────────────

export type CarId = 'racer' | 'jeep' | 'buggy' | 'monster' | 'tank' | 'rocket' | 'moon';

export type CarSpec = {
  id: CarId; name: string; icon: string; blurb: string; color: string;
  grav: number; mass: number; inertia: number;
  spring: number; damp: number; grip: number;
  engine: number; brake: number; reverse: number; top: number;
  airSpin: number; wheelR: number;
  wheels: [[number, number], [number, number]];
  /** The roof: touching the ground with it is a crash. */
  head: [number, number];
};

const BASE = { grav: 24, mass: 1, inertia: 1.1, spring: 900, damp: 40, grip: 1.4, engine: 7, brake: 12, reverse: 4, top: 15, airSpin: 7, wheelR: 0.38 };

export const CARS: CarSpec[] = [
  { ...BASE, id: 'racer', name: 'Racer', icon: '🏎️', color: '#e8401c', blurb: 'Quick and even. Everyone starts with it.',
    wheels: [[-0.75, 0.35], [0.75, 0.35]], head: [0, -0.78] },
  { ...BASE, id: 'jeep', name: 'Jeep', icon: '🚙', color: '#4f8a3c', blurb: 'Grippy tires for steep hills. Not as fast.',
    inertia: 1.3, spring: 750, damp: 36, grip: 2.1, engine: 6.5, top: 12, airSpin: 5, wheelR: 0.42,
    wheels: [[-0.8, 0.4], [0.8, 0.4]], head: [0, -0.85] },
  { ...BASE, id: 'buggy', name: 'Dune Buggy', icon: '🛞', color: '#ffb02e', blurb: 'Light and bouncy. Flips easily, spins fast in the air.',
    mass: 0.7, inertia: 0.65, spring: 420, damp: 16, grip: 1.5, engine: 4.6, top: 17, airSpin: 11, wheelR: 0.4,
    wheels: [[-0.7, 0.42], [0.7, 0.42]], head: [0, -0.8] },
  { ...BASE, id: 'monster', name: 'Monster Truck', icon: '🛻', color: '#7b3fe4', blurb: 'Giant wheels roll over anything. Heavy in the air.',
    mass: 1.4, inertia: 2.1, spring: 650, damp: 30, grip: 1.8, engine: 8.5, top: 12, airSpin: 4, wheelR: 0.62,
    wheels: [[-0.85, 0.55], [0.85, 0.55]], head: [0, -0.9] },
  { ...BASE, id: 'tank', name: 'Tank', icon: '🪖', color: '#5d6b45', blurb: 'Slow, but it grips everything and almost never flips.',
    mass: 1.8, inertia: 2.8, spring: 1400, damp: 75, grip: 3, engine: 9, brake: 16, top: 8, airSpin: 3, wheelR: 0.34,
    wheels: [[-0.85, 0.32], [0.85, 0.32]], head: [0, -0.72] },
  { ...BASE, id: 'rocket', name: 'Rocket Car', icon: '🚀', color: '#1f8fff', blurb: 'The fastest car of all. Slippery, so ease onto the gas.',
    inertia: 1.5, spring: 1000, damp: 45, grip: 1, engine: 10, top: 24, airSpin: 4.5, wheelR: 0.34,
    wheels: [[-0.9, 0.3], [0.9, 0.3]], head: [0, -0.74] },
  { ...BASE, id: 'moon', name: 'Moon Rover', icon: '🌙', color: '#c9d2de', blurb: 'Low gravity: huge, floaty jumps.',
    grav: 11, inertia: 1.2, spring: 480, damp: 24, grip: 1.6, engine: 5, top: 13, airSpin: 5, wheelR: 0.44,
    wheels: [[-0.8, 0.4], [0.8, 0.4]], head: [0, -0.82] },
];
export const carById = (id: string | null | undefined): CarSpec => CARS.find(c => c.id === id) ?? CARS[0];

const INK = '#1e2633';

/** How far above the ground the car's middle sits when it's placed on it. */
export const rideHeight = (c: CarSpec) => c.wheels[0][1] + c.wheelR + 0.05;

/** A car with the player's fishbowl as the cockpit. (x, y) is its middle, `angle` its tilt; s px per tile. */
export function drawCar(g: CanvasRenderingContext2D, look: Look, car: CarSpec, o: { x: number; y: number; angle: number; s: number; t: number; wheel: number; wheelsY: [number, number]; gas: boolean }) {
  const { s } = o;
  g.save();
  g.translate(o.x, o.y); g.rotate(o.angle);
  g.lineJoin = 'round'; g.lineCap = 'round';
  const lw = Math.max(1.2, s / 16);
  g.lineWidth = lw; g.strokeStyle = INK;
  const R = car.wheelR * s;
  if (car.id === 'tank') {
    // Treads around both wheels.
    const [[ax], [bx]] = car.wheels, wy = ((o.wheelsY[0] + o.wheelsY[1]) / 2) * s;
    g.fillStyle = '#2c2f3a'; g.beginPath(); g.roundRect(ax * s - R * 1.1, wy - R * 1.1, (bx - ax) * s + R * 2.2, R * 2.2, R * 1.1); g.fill(); g.stroke();
    for (let k = 0; k < 5; k++) { const x = ax * s + ((bx - ax) * s * k) / 4; g.fillStyle = '#6b7280'; g.beginPath(); g.arc(x, wy, R * 0.6, 0, Math.PI * 2); g.fill(); g.stroke(); }
  } else {
    car.wheels.forEach(([wx], k) => {
      const wy = o.wheelsY[k] * s;
      g.strokeStyle = '#5b6b7f'; g.lineWidth = s * 0.08; g.beginPath(); g.moveTo(wx * s, s * 0.05); g.lineTo(wx * s, wy); g.stroke();
      g.lineWidth = lw; g.strokeStyle = INK;
      g.fillStyle = '#2c2f3a'; g.beginPath(); g.arc(wx * s, wy, R, 0, Math.PI * 2); g.fill(); g.stroke();
      if (car.id === 'monster' || car.id === 'jeep' || car.id === 'buggy') {   // knobbly tires
        g.fillStyle = '#2c2f3a';
        for (let j = 0; j < 10; j++) { const a = o.wheel + (j * Math.PI) / 5; g.beginPath(); g.arc(wx * s + Math.cos(a) * R, wy + Math.sin(a) * R, R * 0.13, 0, Math.PI * 2); g.fill(); }
      }
      g.fillStyle = car.id === 'moon' ? '#ffd23d' : '#c9d2de'; g.beginPath(); g.arc(wx * s, wy, R * 0.45, 0, Math.PI * 2); g.fill();
      g.strokeStyle = '#c9d2de'; g.lineWidth = lw;
      for (let j = 0; j < 4; j++) { const a = o.wheel + (j * Math.PI) / 2; g.beginPath(); g.moveTo(wx * s, wy); g.lineTo(wx * s + Math.cos(a) * R * 0.85, wy + Math.sin(a) * R * 0.85); g.stroke(); }
      g.strokeStyle = INK;
    });
  }
  // The body.
  g.fillStyle = car.color;
  const P = (pts: number[][]) => { g.beginPath(); pts.forEach(([x, y], i) => (i ? g.lineTo(x * s, y * s) : g.moveTo(x * s, y * s))); g.closePath(); g.fill(); g.stroke(); };
  let cockpit = -0.32;
  switch (car.id) {
    case 'racer':
      g.beginPath();
      g.moveTo(-1.05 * s, 0.1 * s); g.lineTo(-1.0 * s, -0.25 * s); g.lineTo(-0.4 * s, -0.32 * s); g.lineTo(0.35 * s, -0.3 * s);
      g.quadraticCurveTo(0.95 * s, -0.25 * s, 1.1 * s, 0.0); g.lineTo(1.05 * s, 0.15 * s); g.closePath(); g.fill(); g.stroke();
      g.fillStyle = '#2c2f3a'; g.fillRect(-1.12 * s, -0.55 * s, 0.32 * s, 0.1 * s); g.beginPath(); g.moveTo(-0.98 * s, -0.45 * s); g.lineTo(-0.9 * s, -0.25 * s); g.stroke();
      g.fillStyle = 'rgba(255,255,255,0.85)'; g.fillRect(-0.2 * s, -0.12 * s, 0.6 * s, 0.07 * s);
      break;
    case 'jeep':
      P([[-1.1, 0.15], [-1.1, -0.35], [0.5, -0.35], [0.7, -0.15], [1.1, -0.12], [1.1, 0.15]]);
      g.fillStyle = '#2c2f3a'; g.fillRect(-1.25 * s, -0.25 * s, 0.15 * s, 0.4 * s);                 // spare tire
      g.strokeStyle = INK; g.beginPath(); g.moveTo(-0.6 * s, -0.35 * s); g.lineTo(-0.6 * s, -0.8 * s); g.lineTo(0.45 * s, -0.8 * s); g.lineTo(0.5 * s, -0.35 * s); g.stroke();   // roll bar
      cockpit = -0.35;
      break;
    case 'buggy':
      g.strokeStyle = car.color; g.lineWidth = s * 0.09;
      g.beginPath(); g.moveTo(-0.9 * s, 0.1 * s); g.lineTo(-0.5 * s, -0.7 * s); g.lineTo(0.4 * s, -0.7 * s); g.lineTo(0.95 * s, 0.05 * s); g.lineTo(-0.9 * s, 0.1 * s); g.stroke();
      g.lineWidth = lw; g.strokeStyle = INK; g.fillStyle = car.color;
      P([[-0.6, 0.12], [-0.55, -0.15], [0.6, -0.15], [0.7, 0.12]]);
      cockpit = -0.15;
      break;
    case 'monster':
      P([[-1.15, -0.05], [-1.1, -0.5], [-0.3, -0.55], [0.1, -0.95], [0.75, -0.95], [0.95, -0.5], [1.2, -0.45], [1.2, -0.05]]);
      g.fillStyle = '#9ff7ff'; P([[0.2, -0.85], [0.68, -0.85], [0.82, -0.55], [0.2, -0.55]]);
      g.fillStyle = '#ffd23d'; for (const x of [-0.8, -0.4]) { g.beginPath(); g.moveTo(x * s, -0.3 * s); g.lineTo((x + 0.15) * s, -0.45 * s); g.lineTo((x + 0.3) * s, -0.3 * s); g.fill(); }   // flames
      cockpit = -0.55;
      break;
    case 'tank':
      P([[-1.1, 0.0], [-0.95, -0.35], [0.95, -0.35], [1.1, 0.0]]);
      g.fillStyle = car.color; g.beginPath(); g.arc(0, -0.35 * s, 0.45 * s, Math.PI, 0); g.fill(); g.stroke();
      g.fillStyle = '#3e4a2f'; g.fillRect(0.35 * s, -0.6 * s, 0.9 * s, 0.12 * s); g.strokeRect(0.35 * s, -0.6 * s, 0.9 * s, 0.12 * s);   // the barrel
      cockpit = -0.4;
      break;
    case 'rocket':
      P([[-1.2, 0.1], [-1.1, -0.25], [0.2, -0.3], [1.3, -0.05], [1.2, 0.12]]);
      g.fillStyle = '#ffffff'; P([[-1.15, -0.25], [-1.35, -0.6], [-0.85, -0.25]]);
      if (o.gas) {   // the flame
        const f = 0.4 + Math.sin(o.t * 40) * 0.12;
        g.fillStyle = '#ffb02e'; g.beginPath(); g.moveTo(-1.2 * s, -0.12 * s); g.lineTo((-1.2 - f) * s, 0); g.lineTo(-1.2 * s, 0.08 * s); g.fill();
        g.fillStyle = '#fff3a3'; g.beginPath(); g.moveTo(-1.2 * s, -0.06 * s); g.lineTo((-1.2 - f * 0.5) * s, 0); g.lineTo(-1.2 * s, 0.04 * s); g.fill();
      }
      break;
    case 'moon':
      g.fillStyle = car.color; P([[-1.0, 0.1], [-1.0, -0.25], [1.0, -0.25], [1.0, 0.1]]);
      g.fillStyle = '#ffd23d'; g.fillRect(-0.95 * s, -0.2 * s, 0.5 * s, 0.25 * s); g.strokeRect(-0.95 * s, -0.2 * s, 0.5 * s, 0.25 * s);   // solar panel
      g.strokeStyle = INK; g.beginPath(); g.moveTo(0.8 * s, -0.25 * s); g.lineTo(0.8 * s, -0.95 * s); g.stroke();
      g.fillStyle = '#ff5c5c'; g.beginPath(); g.arc(0.8 * s, -0.95 * s, 0.08 * s, 0, Math.PI * 2); g.fill(); g.stroke();
      cockpit = -0.25;
      break;
  }
  g.fillStyle = '#fff'; g.beginPath(); g.arc(1.0 * s, -0.05 * s, 0.07 * s, 0, Math.PI * 2); g.fill();   // headlight
  // The fishbowl cockpit, with the fish driving.
  const r = 0.42 * s, cy = cockpit * s;
  const bowl = new Path2D(); bowl.arc(0, cy, r, Math.PI, 0); bowl.closePath();
  g.fillStyle = 'rgba(210,240,255,0.45)'; g.fill(bowl);
  g.save(); g.clip(bowl);
  g.fillStyle = 'rgba(70,170,255,0.5)'; g.fillRect(-r, cy - r * 0.55, r * 2, r);
  drawAvatar(g, look, { x: 0, y: cy - r * 0.35, size: r * 1.3, facing: 1, effort: o.gas ? 1 : 0.3, t: o.t });
  g.restore();
  g.strokeStyle = INK; g.lineWidth = lw; g.stroke(bowl);
  g.strokeStyle = 'rgba(255,255,255,0.85)'; g.lineWidth = lw * 1.4; g.beginPath(); g.arc(0, cy, r * 0.7, Math.PI * 1.15, Math.PI * 1.45); g.stroke();
  g.restore();
}
